//******************************************************************************************
// AdminDashboard Component: Main dashboard for admin users to manage conversations,
// settings, user roles, and Q&A entries. Provides UI for accessing various management
// tools and for viewing/administering conversations with users.
//******************************************************************************************

import React, { useState, useEffect } from "react";
import AdminConversations from "./Conversation/AdminConversations";
import AdminMessageContainer from "./Message/AdminMessageContainer";
import LogoutButton from "./Message/AdminLogoutButton";
import "./AdminDashboard.scss";
import { useAdminMessagesContext, AdminMessagesContextProvider } from "../../context/AdminMessagesContext";
import { DASH_TEXT_CONSTANTS } from "../../utils/constants";
import { useAuthContext } from "../../context/AuthContext";
import SettingsPopup from "./ControlPopups/SettingsPopup";
import UserManagementPopup from "./ControlPopups/UserManagementPopup";
import QuestionAnswerManagementPopup from "./ControlPopups/QuestionAnswerManagementPopup";
import LogsPopup from "./ControlPopups/LogsPopup";
import ChangePasswordPopup from "./ControlPopups/ChangePasswordPopup";
import moment from "moment";
import { AdminSocketProvider } from "../../context/AdminSocketContext";
import UsagePopup from "./ControlPopups/UsagePopup";

// A popup overlay for picking dates on smaller or mid screens
const DateFilterPopup = ({ show, onClose, localStartDate, localEndDate, onStartDateChange, onEndDateChange, onApply }) => {
  if (!show) return null;

  return (
    <div className="date-filter-popup__overlay">
      <div className="date-filter-popup">
        <h3>Selecionar Datas</h3>
        <div className="date-filter-popup__inputs">
          <label>
            <span>Data Início:</span>
            <input type="date" value={localStartDate} onChange={onStartDateChange} />
          </label>
          <label>
            <span>Data Fim:</span>
            <input type="date" value={localEndDate} onChange={onEndDateChange} />
          </label>
        </div>
        <div className="date-filter-popup__buttons">
          <button onClick={onApply}>Aplicar</button>
          <button onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  return (
    <AdminSocketProvider>
      <AdminMessagesContextProvider>
        <AdminDashboardContent />
      </AdminMessagesContextProvider>
    </AdminSocketProvider>
  );
};

const AdminDashboardContent = () => {
  const { setStartDate, setEndDate, fetchConversations, selectedConversation, setSelectedConversation, conversations, setConversations } =
    useAdminMessagesContext();

  const { authUser } = useAuthContext();

  // Popups
  const [showSettings, setShowSettings] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showQAManagement, setShowQAManagement] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showUsagePopup, setShowUsagePopup] = useState(false);

  // Date filters
  const [localStartDate, setLocalStartDate] = useState(moment().subtract(6, "days").format("YYYY-MM-DD"));
  const [localEndDate, setLocalEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [showDatePopup, setShowDatePopup] = useState(false);

  // Determine screen size for layout logic
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 1000);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 1000);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSettingsClick = () => setShowSettings(true);
  const handleUserManagementClick = () => setShowUserManagement(true);
  const handleQAManagementClick = () => setShowQAManagement(true);
  const handleLogsClick = () => setShowLogs(true);
  const handleChangePasswordClick = () => setShowChangePassword(true);
  const handleUsageClick = () => {
    setShowUsagePopup(!showUsagePopup);
  };

  // Date inputs
  const handleLocalStartDateChange = (e) => setLocalStartDate(e.target.value);
  const handleLocalEndDateChange = (e) => setLocalEndDate(e.target.value);
  const handleDateApply = () => {
    setStartDate(localStartDate);
    setEndDate(localEndDate);
    fetchConversations();
    setShowDatePopup(false);
  };

  // Conversation selection
  const handleSelectConversation = (conversation) => {
    if (selectedConversation && selectedConversation._id === conversation._id) {
      setSelectedConversation(null);
      return;
    }
    setSelectedConversation(conversation);

    // Mark conversation as read locally
    setConversations((prev) => prev.map((conv) => (conv._id === conversation._id ? { ...conv, adminSeen: true } : conv)));
  };

  // Pre-build the logout button
  const logoutButton = (
    <LogoutButton
      onSettingsClick={handleSettingsClick}
      onUserManagementClick={handleUserManagementClick}
      onQAManagementClick={handleQAManagementClick}
      onLogsClick={handleLogsClick}
      onChangePasswordClick={handleChangePasswordClick}
      handleUsageClick={handleUsageClick}
      settingsVisible={authUser?.isSuperAdmin}
      FAQVisible={authUser?.canManageFAQ}
      authUser={authUser}
    />
  );

  // Pre-build date filters (inline for large screens or popup trigger for smaller)
  const dateFilters =
    authUser?.isSuperAdmin && isLargeScreen ? (
      <div className="admin-sidebar__date-filters">
        <label>
          <span>Data Início:</span>
          <span>Data Fim:</span>
        </label>
        <label>
          <input type="date" value={localStartDate} onChange={handleLocalStartDateChange} />
          <input type="date" value={localEndDate} onChange={handleLocalEndDateChange} />
        </label>
        <button onClick={handleDateApply}>Aplicar</button>
      </div>
    ) : authUser?.isSuperAdmin && !isLargeScreen ? (
      <button className="date-filter-button" onClick={() => setShowDatePopup(true)}>
        Filtrar Datas
      </button>
    ) : null;

  // For large screens:
  //   [Left Column] -> logos, conversation list, date filters, logout button
  //   [Right Column] -> message container
  // For smaller screens:
  //   [Left Column top] -> logos, conversation list, date filters
  //   [Right Column below that] -> message container
  //   [Finally below everything] -> logout button
  if (isLargeScreen) {
    return (
      <div className="admin-sidebar">
        <div className="admin-sidebar__conversations">
          {/* Logos at top (centered) */}
          <div className="logos">
            <img src="/A360.svg" alt="A360 Logo" className="a360_logo" />
            <img src="/pacos_ferreira.png" alt="Pacos de Ferreira Logo" className="municipal_logo" />
          </div>

          {/* Conversation list */}
          <div className="admin-sidebar__conversations-list">
            <AdminConversations onSelectConversation={handleSelectConversation} selectedConversationId={selectedConversation?._id} />
          </div>

          {/* Date filters inline (only on large screens) */}
          {dateFilters}

          {/* Logout button at the bottom of the left column */}
          <div className="admin-sidebar__logout">{logoutButton}</div>
        </div>

        {/* Main chat area */}
        <div className={`admin-sidebar__message-container ${!selectedConversation ? "centered" : ""}`}>
          {selectedConversation ? (
            <AdminMessageContainer conversationId={selectedConversation._id} />
          ) : (
            <div className="admin-sidebar__message-box">
              <p>{conversations.length === 0 ? DASH_TEXT_CONSTANTS.NO_CONVERSATION_PROMPT : DASH_TEXT_CONSTANTS.SELECT_CONVERSATION_PROMPT}</p>
            </div>
          )}
        </div>

        {/* Popups */}
        {showSettings && <SettingsPopup onClose={() => setShowSettings(false)} />}
        {showUserManagement && <UserManagementPopup onClose={() => setShowUserManagement(false)} />}
        {showLogs && <LogsPopup onClose={() => setShowLogs(false)} />}
        {showQAManagement && <QuestionAnswerManagementPopup onClose={() => setShowQAManagement(false)} />}
        {showChangePassword && <ChangePasswordPopup onClose={() => setShowChangePassword(false)} />}
        {showUsagePopup && <UsagePopup onClose={() => setShowUsagePopup(false)} />}

        {/* Date Filter Popup */}
        <DateFilterPopup
          show={showDatePopup && authUser?.isSuperAdmin}
          onClose={() => setShowDatePopup(false)}
          localStartDate={localStartDate}
          localEndDate={localEndDate}
          onStartDateChange={handleLocalStartDateChange}
          onEndDateChange={handleLocalEndDateChange}
          onApply={handleDateApply}
        />
      </div>
    );
  } else {
    // Smaller or mid screens
    return (
      <div className="admin-sidebar">
        {/* Left column top */}
        <div className="admin-sidebar__conversations">
          <div className="logos">
            <img src="/A360.svg" alt="A360 Logo" className="a360_logo" />
            <img src="/pacos_ferreira.png" alt="Pacos de Ferreira Logo" className="municipal_logo" />
          </div>

          <div className="admin-sidebar__conversations-list">
            <AdminConversations onSelectConversation={handleSelectConversation} selectedConversationId={selectedConversation?._id} />
          </div>
          {/* Date filter as popup trigger */}
          {dateFilters}
        </div>

        {/* Message container in the middle */}
        <div className={`admin-sidebar__message-container ${!selectedConversation ? "centered" : ""}`}>
          {selectedConversation ? (
            <AdminMessageContainer conversationId={selectedConversation._id} />
          ) : (
            <div className="admin-sidebar__message-box">
              <p>{conversations.length === 0 ? DASH_TEXT_CONSTANTS.NO_CONVERSATION_PROMPT : DASH_TEXT_CONSTANTS.SELECT_CONVERSATION_PROMPT}</p>
            </div>
          )}
        </div>

        {/* Logout button physically placed at the bottom for small screens */}
        <div className="admin-sidebar__logout">{logoutButton}</div>

        {/* Popups */}
        {showSettings && <SettingsPopup onClose={() => setShowSettings(false)} />}
        {showUserManagement && <UserManagementPopup onClose={() => setShowUserManagement(false)} />}
        {showLogs && <LogsPopup onClose={() => setShowLogs(false)} />}
        {showQAManagement && <QuestionAnswerManagementPopup onClose={() => setShowQAManagement(false)} />}
        {showChangePassword && <ChangePasswordPopup onClose={() => setShowChangePassword(false)} />}
        {showUsagePopup && <UsagePopup onClose={() => setShowUsagePopup(false)} />}

        {/* Date Filter Popup for mid & small screens */}
        <DateFilterPopup
          show={showDatePopup && authUser?.isSuperAdmin}
          onClose={() => setShowDatePopup(false)}
          localStartDate={localStartDate}
          localEndDate={localEndDate}
          onStartDateChange={handleLocalStartDateChange}
          onEndDateChange={handleLocalEndDateChange}
          onApply={handleDateApply}
        />
      </div>
    );
  }
};

export default AdminDashboard;
